import {
  ChevronLeftRounded,
  CreditCardRounded,
  LeaderboardRounded,
  PeopleRounded,
  SettingsRounded,
} from '@mui/icons-material';
import { List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { ReactComponent as ProfileIcon } from 'assets/icon-profile.svg';
import { getRoutePath, RouteName, routesConfig, shouldPageBeVisible } from 'config/routes';
import {
  getCustomerRole,
  getCustomerTeams,
  getIsApiCustomer,
} from 'features/customer/store/selectors';
import FormattedMessage from 'features/i18n/FormattedMessage';
import { SidebarNavItem } from 'features/pageSidebar/sidebarItems/SidebarNavItem';
import { useSidebarStore } from 'features/pageSidebar/useSidebarStore';
import gtmIds from 'services/tracking/GTMIds';
import { useAppSelector } from 'store/hooks';
import styled from 'styled-components';
import { SpecialCharacter } from 'utils/SpecialCharacter';

export const SidebarSettingsView = () => {
  const customerGroup = useAppSelector(state => state.customer.groupId);
  const userRole = useAppSelector(getCustomerRole);
  const isApiCustomer = useAppSelector(getIsApiCustomer);
  const customerTeams = useAppSelector(getCustomerTeams);

  const isSidebarExpanded = useSidebarStore(state => state.isExpanded);

  const shouldRenderSidebarItem = (routeName: RouteName) =>
    shouldPageBeVisible(
      routesConfig[routeName],
      customerGroup,
      userRole,
      isApiCustomer,
      customerTeams
    );

  return (
    <Stack gap={3}>
      <SidebarNavItem
        to={getRoutePath('home')}
        icon={<ChevronLeftRounded />}
        name="layout.sidebar.settings_menu.back_to_neuroflash"
        toggleSidebarOnClick={false}
      />

      <ScrollableList>
        <CaptionListItem>
          <ListItemText>
            <Typography variant="caption" color="text.secondary" textTransform="uppercase">
              {isSidebarExpanded ? (
                <FormattedMessage id="layout.sidebar.settings_menu.headline_profile" />
              ) : (
                SpecialCharacter.noBreakSpace
              )}
            </Typography>
          </ListItemText>
        </CaptionListItem>

        <SidebarNavItem
          gtmId={gtmIds.sidebar.accountMenuItem}
          to={getRoutePath('customerSettingsGeneral')}
          icon={<ProfileIcon />}
          name="layout.sidebar.actions.profile.profile"
          toggleSidebarOnClick={false}
        />
      </ScrollableList>

      <ScrollableList>
        <CaptionListItem>
          <ListItemText>
            <Typography variant="caption" color="text.secondary" textTransform="uppercase">
              {isSidebarExpanded ? (
                <FormattedMessage id="layout.sidebar.settings_menu.headline_workspace" />
              ) : (
                SpecialCharacter.noBreakSpace
              )}
            </Typography>
          </ListItemText>
        </CaptionListItem>

        {shouldRenderSidebarItem('workspaceSettingsGeneral') && (
          <SidebarNavItem
            gtmId={gtmIds.sidebar.workspace.general}
            to={getRoutePath('workspaceSettingsGeneral')}
            icon={<SettingsRounded />}
            name="layout.sidebar.actions.profile.workspace.settings_general"
            toggleSidebarOnClick={false}
          />
        )}

        {shouldRenderSidebarItem('workspaceSettingsUsers') && (
          <SidebarNavItem
            gtmId={gtmIds.sidebar.workspace.users}
            to={getRoutePath('workspaceSettingsUsers')}
            icon={<PeopleRounded />}
            name="layout.sidebar.actions.profile.workspace.settings_users"
            toggleSidebarOnClick={false}
          />
        )}

        {shouldRenderSidebarItem('workspaceSettingsUsage') && (
          <SidebarNavItem
            gtmId={gtmIds.sidebar.workspace.usage}
            to={getRoutePath('workspaceSettingsUsage')}
            icon={<LeaderboardRounded />}
            name="layout.sidebar.actions.profile.workspace.settings_usage"
            toggleSidebarOnClick={false}
          />
        )}

        {shouldRenderSidebarItem('workspaceSettingsBilling') && (
          <SidebarNavItem
            gtmId={gtmIds.sidebar.workspace.billing}
            to={getRoutePath('workspaceSettingsBilling')}
            icon={<CreditCardRounded />}
            name="layout.sidebar.actions.profile.workspace.settings_billing"
            toggleSidebarOnClick={false}
          />
        )}
      </ScrollableList>
    </Stack>
  );
};

const ScrollableList = styled(List)`
  overflow-y: hidden;
  overflow-x: hidden;
  padding: 0;
`;

const CaptionListItem = styled(ListItem)`
  margin-bottom: ${({ theme }) => theme.spacings.one};

  &.MuiListItem-root {
    padding-block: 0;
  }
`;
